import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCourse } from '@/context/useCourseContext';
import { useCourseData } from '@/pages/Course';
import { KeyNotes, FlashCard, Quiz, Community } from '.';
import DocChat from './DocChat';
import Topic from './Topic'
import Resources from '../Resources';
import { Skeleton } from '@/components/ui/skeleton';

export function TopicSkeleton() {
  return (
    <div className="p-6 space-y-6">
      <Skeleton className="h-10 w-3/4 rounded-lg" />
      <div className="space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
      <Skeleton className="h-64 w-full rounded-md" />
      <div className="space-y-3 pt-4">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  );
}

function Right() {
  const { selectedTopic, selectedToggle } = useCourse();
  const { course } = useCourseData();

  if (!course) {
    return (
      <div className="flex-1 h-full p-1">
        <TopicSkeleton />
      </div>
    );
  }

  const renderContent = () => {
    // ✅ FIXED LOGIC HERE:
    // Only show the Topic component if a topic is selected AND no other toggle is active.
    if (selectedTopic && !selectedToggle) {
      return <Topic topic={selectedTopic} />;
    }

    // When a toggle is selected, this switch statement will now be correctly reached.
    switch (selectedToggle) {
      case 'docchat':
        return <DocChat course={course} />;
      case 'community':
        return <Community communityId={course.communityId} />;
      case 'quiz':
        return <Quiz course={course} />;
      case 'summary':
        return <KeyNotes keyNotes={course?.keyNotes} />;
      case 'resources':
        return <Resources course={course} />;
      case 'flashcard':
        return <FlashCard course={course} />;
      default:
        // This default case handles when a topic is de-selected or no view is active.
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-center font-semibold text-brand py-6">Please select a topic to begin.</div>
          </div>
        );
    }
  };

  // The animation key now correctly distinguishes between the topic view and other toggles.
  const animationKey = selectedToggle || (selectedTopic ? 'topic' : 'default-view');

  return (
    <div className="flex-1 h-full p-1">
      <AnimatePresence mode="wait">
        <motion.div
          key={animationKey}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -15 }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
          className="h-full w-full">
          <div className="h-full overflow-y-auto">{renderContent()}</div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default Right;
